import { ColDef, ColGroupDef } from "ag-grid-community";

import { doesSaleLotHaveOverflowPen } from "lib/saleLot";
import { actionsColumnRenderer as privateSaleActionsColumnRenderer } from "components/PrivateSales/ActionColumn";

import {
  ACTION_COLUMN_ID,
  ACTION_COLUMN_NAME,
  ACTIONS_MOBILE_WIDTH,
} from "constants/aggrid";
import {
  BuyerAddressColumnDef,
  BuyerContactColumnDef,
  LivestockSaleColumnDefinitions,
  VendorAddressColumnDef,
  VendorContactColumnDef,
} from "constants/columnDefinitions";
import {
  PropertyColumnDefinitions,
  PropertyNamespaceColumnId,
} from "constants/columnDefinitions/property";
import { Column, LivestockSaleColId } from "constants/columns";
import { SaleType } from "constants/sale";

import { createNamespacedColumnDefinitions } from "lib/agGrid";
import {
  BaseColumnDefinitions,
  ConsignmentColumnDefinitions,
} from "lib/agGrid/columnDefinitions";
import {
  advancedDraftingColumns,
  dentitionColumns,
  speciesAccreditationChildColumns,
} from "lib/agGrid/columns/advancedDrafting";
import { AggridColDefContext } from "lib/agGrid/types";
import { saleLotCommentsColumnId } from "lib/agGrid/renderers";

import { ActionsColumnRenderer } from "./columns/actionsColumn";

type SaleLotColDef = ColDef | ColGroupDef;

const ConsignmentColumnDefsOnLot = createNamespacedColumnDefinitions(
  ConsignmentColumnDefinitions,
  "consignment",
);

const saleLotSpeciesAccreditationChildColumns = (speciesId: number) =>
  speciesAccreditationChildColumns(speciesId, BaseColumnDefinitions);

const saleLotDentitionColumns = (speciesId: number, enableDentition: boolean) =>
  dentitionColumns(speciesId, enableDentition, BaseColumnDefinitions);

const saleLotAdvancedDraftingColumns = (speciesId: number) =>
  advancedDraftingColumns(speciesId, BaseColumnDefinitions);

const defaultSaleLotColumns = (
  studFeaturesEnabled: boolean,
  penScanLotFeaturesEnabled: boolean,
  speciesId: number,
  saleSubType: SaleSubType,
): SaleLotColDef[] => {
  const columns: SaleLotColDef[] = [
    BaseColumnDefinitions[Column.Totals.LOW_DOLLARS_HEAD],
    BaseColumnDefinitions[Column.Totals.AVG_DOLLARS_HEAD],
    BaseColumnDefinitions[Column.Totals.HIGH_DOLLARS_HEAD],
    BaseColumnDefinitions[Column.Totals.LOW_CENTS_KG],
    BaseColumnDefinitions[Column.Totals.AVG_CENTS_KG],
    BaseColumnDefinitions[Column.Totals.HIGH_CENTS_KG],
    BaseColumnDefinitions[Column.Totals.HEAD],
    BaseColumnDefinitions[Column.Totals.CENTS],
    BaseColumnDefinitions[Column.PEN],
    BaseColumnDefinitions[Column.AGENCY_VENDOR_NUM],
    BaseColumnDefinitions[Column.VENDOR_NAME],
    BaseColumnDefinitions[Column.VENDOR_PIC],
    {
      headerName: "NVD",
      children: [
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_ID],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_VERSION],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_HC],
        {
          ...ConsignmentColumnDefsOnLot[Column.Consignment.NVD_RECEIVED],
          filterValueGetter: params => {
            const consignment = params.data?.consignment ?? params.data;
            return consignment?.hasNVDExceptions
              ? "Has NVD Exceptions"
              : "No NVD Exceptions";
          },
          filter: "agSetColumnFilter",
        },
        ConsignmentColumnDefsOnLot[Column.Consignment.ADDITIONAL_PICS],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_1],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_2],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_3],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_4],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_5],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_6],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_7],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_8],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_9],
      ],
    },
    ConsignmentColumnDefsOnLot[Column.Consignment.BRANDS],
    {
      headerName: "Vendor",
      children: [
        BaseColumnDefinitions[Column.VENDOR_SHORT_CODE],
        BaseColumnDefinitions[Column.VENDOR_SHORT_CODE_SALEYARD],
        BaseColumnDefinitions[Column.VENDOR_ALTERNATIVES],
        BaseColumnDefinitions[Column.SaleLot.VENDOR_SPLITS],
        BaseColumnDefinitions[Column.SaleLot.VENDOR_COMMISSION],
        ...Object.values(VendorContactColumnDef),
        {
          headerName: "Address",
          children: Object.values(VendorAddressColumnDef),
        },
      ],
    },
    {
      headerName: "Vendor Property",
      children: Object.values(
        createNamespacedColumnDefinitions(
          //
          PropertyColumnDefinitions,
          "consignment.vendor_property",
          PropertyNamespaceColumnId.VENDOR_PROPERTY,
        ),
      ),
    },
    BaseColumnDefinitions[Column.BUSINESS_DISPLAY_NAME],
    BaseColumnDefinitions[Column.BUYER_WAY_NAME],
    BaseColumnDefinitions[Column.BIDDER_REGO_NUMBER],
    {
      headerName: "Buyer",
      children: [
        BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
        BaseColumnDefinitions[Column.BUYER_SHORT_CODE_SALEYARD],
        BaseColumnDefinitions[Column.BUYER_NAME],
        BaseColumnDefinitions[Column.BUYER_ALTERNATIVES],
        BaseColumnDefinitions[Column.SaleLot.BUYER_SPLITS],
        ...Object.values(BuyerContactColumnDef),
        {
          headerName: "Address",
          children: Object.values(BuyerAddressColumnDef),
        },
      ],
    },
    {
      headerName: "Transporter",
      children: [
        BaseColumnDefinitions[Column.Transporter.NAME],
        BaseColumnDefinitions[Column.Transporter.SHORT_CODE_SALEYARD],
      ],
    },
    {
      headerName: "Buyer Property",
      children: Object.values(
        createNamespacedColumnDefinitions(
          PropertyColumnDefinitions,
          "destinationProperty",
          PropertyNamespaceColumnId.BUYER_PROPERTY,
        ),
      ),
    },

    BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    BaseColumnDefinitions[Column.INVOICE_TO_CODE],
    BaseColumnDefinitions[Column.QUANTITY_PROGENY],
    BaseColumnDefinitions[Column.DESTINATION_PIC],
    {
      ...BaseColumnDefinitions[Column.LOT_NUMBER],
      filterValueGetter: params => {
        const tags = [];
        if (doesSaleLotHaveOverflowPen(params.data))
          tags.push("Has Overflow Pen");
        if (params.data?.hasExceptions) tags.push("Has Exceptions");
        return tags;
      },
      filter: "agSetColumnFilter",
    },
    BaseColumnDefinitions[Column.LOT_NUMBER_SUFFIX],
    BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
    BaseColumnDefinitions[Column.THIRD_PARTY_NAME],
    BaseColumnDefinitions[Column.UNIT_PRICE],
    BaseColumnDefinitions[Column.TOTAL_PRICE],
    BaseColumnDefinitions[Column.RESERVE_PRICE],
    BaseColumnDefinitions[Column.SCANNED_COUNT],
    BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
    BaseColumnDefinitions[Column.QUANTITY],
    BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
    BaseColumnDefinitions[Column.QUANTITY_TAGS_USED],
    BaseColumnDefinitions[Column.NO_TAG_COUNT],
    BaseColumnDefinitions[Column.NON_READER_COUNT],
    BaseColumnDefinitions[Column.DECEASED_COUNT],
    BaseColumnDefinitions[Column.DIFF],
    BaseColumnDefinitions[Column.BREED_NAME],
    BaseColumnDefinitions[Column.SaleLot.BREED_CODE],
    BaseColumnDefinitions[Column.SaleLot.BREED_SALEYARD_CODE],
    BaseColumnDefinitions[Column.SaleLot.PRODUCT_CODE],
    BaseColumnDefinitions[Column.SaleLot.PRODUCT_SALEYARD_CODE],
    BaseColumnDefinitions[Column.SEX_NAME],
    BaseColumnDefinitions[Column.AGE_GROUP_NAME],
    BaseColumnDefinitions[Column.SaleLot.AGE_GROUP_CODE],
    BaseColumnDefinitions[Column.SaleLot.SELLING_ORDER],
    saleLotDentitionColumns(speciesId, saleSubType?.enableDentition),
    BaseColumnDefinitions[Column.CATEGORY_NAME],
    BaseColumnDefinitions[Column.GRADE_NAME],
    BaseColumnDefinitions[Column.WEIGHT],
    BaseColumnDefinitions[Column.WEIGHT_OUTLIER],
    BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
    BaseColumnDefinitions[Column.AVERAGE_WEIGHT_OUTLIER],
    BaseColumnDefinitions[Column.SALE_ROUND_NAME],
    BaseColumnDefinitions[Column.STATUS],
    BaseColumnDefinitions[Column.AGENCY_NAME],
    BaseColumnDefinitions[Column.GST_STATUS],
    BaseColumnDefinitions[Column.IMAGES],
    BaseColumnDefinitions[Column.NOTE],
    BaseColumnDefinitions[Column.LABELS],
    BaseColumnDefinitions[Column.MARKS],
    BaseColumnDefinitions[Column.CREATED],
    BaseColumnDefinitions[Column.LAST_MODIFIED],
    BaseColumnDefinitions[Column.SINGLE_WEIGH_SUM],
    BaseColumnDefinitions[Column.SINGLE_WEIGH_SUM_OUTLIER],
    BaseColumnDefinitions[Column.SINGLE_WEIGH_DIFF],
    BaseColumnDefinitions[Column.TIME_BULK_WEIGHED],
    BaseColumnDefinitions[Column.CONSIGNED_FROM],
    BaseColumnDefinitions[Column.DELIVERY_PEN],
    BaseColumnDefinitions[Column.WEIGHED_COUNT],
    BaseColumnDefinitions[Column.OPEN_AUCTION_COUNT],
    BaseColumnDefinitions[Column.NOT_ANGUS_VERIFIED_COUNT],
    BaseColumnDefinitions[Column.XERO_VENDOR],
    BaseColumnDefinitions[Column.XERO_BUYER],
    BaseColumnDefinitions[Column.OVERFLOW_PEN],
    BaseColumnDefinitions[Column.SaleLot.ACCOUNT_NOTE],
    BaseColumnDefinitions[Column.EXEMPTION_REASON],
    studFeaturesEnabled
      ? BaseColumnDefinitions[Column.SaleLot.ANIMAL_NAME]
      : null,
    studFeaturesEnabled
      ? BaseColumnDefinitions[Column.SaleLot.TAG_NUMBER]
      : null,
    {
      headerName: ACTION_COLUMN_NAME,
      cellRenderer: ActionsColumnRenderer,
      context: {
        mobileWidth: ACTIONS_MOBILE_WIDTH,
      } satisfies AggridColDefContext,
      colId: ACTION_COLUMN_ID,
      width: 180,
      pinned: "right",
      menuTabs: ["columnsMenuTab"],
      enableCellChangeFlash: false,
    },
    BaseColumnDefinitions[Column.SaleLot.ESTIMATED_DRESSING_RANGE],
    BaseColumnDefinitions[Column.SaleLot.ESTIMATED_WEIGHT_RANGE],
    BaseColumnDefinitions[saleLotCommentsColumnId],
    penScanLotFeaturesEnabled &&
      BaseColumnDefinitions[Column.SaleLot.PEN_SCAN_LOTS],
    penScanLotFeaturesEnabled && BaseColumnDefinitions[Column.SaleLot.WARNING],
    {
      headerName: "Sale",
      children: [
        LivestockSaleColumnDefinitions[LivestockSaleColId.SALE_TYPE],
        LivestockSaleColumnDefinitions[LivestockSaleColId.SALE_SUB_TYPE],
        LivestockSaleColumnDefinitions[LivestockSaleColId.DATE],
        LivestockSaleColumnDefinitions[LivestockSaleColId.TITLE],
        LivestockSaleColumnDefinitions[LivestockSaleColId.SPECIES_NAME],
      ],
    },
    {
      headerName: "Drafting Attributes",
      children: saleLotSpeciesAccreditationChildColumns(speciesId),
    },
    BaseColumnDefinitions[Column.SaleLot.NEVER_EVER_ACCREDITATION_STATUS],
    ...saleLotAdvancedDraftingColumns(speciesId),
  ];

  // hide by default rely on saved views to show
  return columns.filter(Boolean).map(colDef => ({ ...colDef, hide: true }));
};

const clearingSaleSaleLotColumns: SaleLotColDef[] = [
  {
    ...BaseColumnDefinitions[Column.VENDOR_SHORT_CODE],
    headerName: "Vendor Ref",
  },
  BaseColumnDefinitions[Column.VENDOR_NAME],
  ...Object.values(VendorContactColumnDef).map(col => ({
    ...col,
    headerName: `Vendor ${col.headerName}`,
  })),
  {
    ...BaseColumnDefinitions[Column.SaleLot.VENDOR_SPLITS],
    headerName: "Vendor Splits",
  },
  BaseColumnDefinitions[Column.LOT_NUMBER],
  BaseColumnDefinitions[Column.LOT_NUMBER_SUFFIX],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.VENDOR_RELATIONSHIPS],
  { ...BaseColumnDefinitions[Column.BUSINESS_DISPLAY_NAME], hide: true },
  {
    ...BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
    headerName: "Buyer Ref",
  },
  BaseColumnDefinitions[Column.ORIGINAL_LISTING_PRICE],
  BaseColumnDefinitions[Column.BUYER_NAME],
  BaseColumnDefinitions[Column.BUYER_NUMBER],
  BaseColumnDefinitions[Column.BIDDER_REGO_NUMBER],
  ...Object.values(BuyerContactColumnDef).map(col => ({
    ...col,
    headerName: `Buyer ${col.headerName}`,
  })),
  BaseColumnDefinitions[Column.INVOICE_TO_NAME],
  BaseColumnDefinitions[Column.INVOICE_TO_CODE],
  BaseColumnDefinitions[Column.BUYER_PAYMENTS],
  BaseColumnDefinitions[Column.UNIT_PRICE],
  BaseColumnDefinitions[Column.IS_GST_EXEMPT],
  BaseColumnDefinitions[Column.GST_STATUS],
  BaseColumnDefinitions[Column.ITEM_LOCATION],
  BaseColumnDefinitions[Column.HAS_BUYER_POSTAL_ADDRESS],
  BaseColumnDefinitions[Column.HAS_VENDOR_POSTAL_ADDRESS],
  BaseColumnDefinitions[Column.HAS_PICKUP_ADDRESS],

  BaseColumnDefinitions[Column.EFFECTIVE_VENDOR_COMMISSION],
  BaseColumnDefinitions[Column.EFFECTIVE_VENDOR_COMMISSION_CENTS],
  BaseColumnDefinitions[Column.EFFECTIVE_LISTING_FEE_CENTS],
  BaseColumnDefinitions[Column.RESERVE_PRICE],
  BaseColumnDefinitions[Column.TOTAL_PRICE],
  BaseColumnDefinitions[Column.STARTING_BID],
  BaseColumnDefinitions[Column.BID_INCREMENT],

  BaseColumnDefinitions[Column.EFFECTIVE_BUYERS_PREMIUM_PERCENT],
  BaseColumnDefinitions[Column.EFFECTIVE_BUYERS_PREMIUM_CENTS],
  {
    ...BaseColumnDefinitions[Column.QUANTITY],
    headerName: "Qty",
  },
  {
    headerName: ACTION_COLUMN_NAME,
    field: "status",
    cellRenderer: ActionsColumnRenderer,
    colId: ACTION_COLUMN_ID,
    width: 260,
    context: {
      mobileWidth: ACTIONS_MOBILE_WIDTH,
    } satisfies AggridColDefContext,
    pinned: "right",
    menuTabs: ["columnsMenuTab"],
  },
  BaseColumnDefinitions[Column.CLEARING_SALE_CATEGORY],
  BaseColumnDefinitions[Column.CLEARING_SALE_COMMENTS],
  BaseColumnDefinitions[Column.CLEARING_SALE_HOURS],
  BaseColumnDefinitions[Column.CLEARING_SALE_INSPECTION],
  BaseColumnDefinitions[Column.CLEARING_SALE_ITEM_CONDITION],
  BaseColumnDefinitions[Column.CLEARING_SALE_ITEM_TYPE],
  BaseColumnDefinitions[Column.CLEARING_SALE_MAKE],
  BaseColumnDefinitions[Column.CLEARING_SALE_MODEL],
  BaseColumnDefinitions[Column.CLEARING_SALE_NEW_USED],
  BaseColumnDefinitions[Column.CLEARING_SALE_ODOMETER],
  BaseColumnDefinitions[Column.CLEARING_SALE_PPSR_COMMENTS],
  BaseColumnDefinitions[Column.CLEARING_SALE_PPSR_STATUS],
  BaseColumnDefinitions[Column.CLEARING_SALE_PUBLIC_DESCRIPTION],
  BaseColumnDefinitions[Column.CLEARING_SALE_REGISTRATION],
  BaseColumnDefinitions[Column.CLEARING_SALE_SELL_SAFE_CATEGORY],
  BaseColumnDefinitions[Column.CLEARING_SALE_STATUS],
  BaseColumnDefinitions[Column.CLEARING_SALE_SUB_TYPE],
  BaseColumnDefinitions[Column.CLEARING_SALE_TITLE],
  BaseColumnDefinitions[Column.CLEARING_SALE_VIN],
  BaseColumnDefinitions[Column.CLEARING_SALE_YEAR],
  BaseColumnDefinitions[Column.LISTING_ID],
  BaseColumnDefinitions[Column.IMAGES],
  BaseColumnDefinitions[Column.NOTE],
  BaseColumnDefinitions[Column.CREATED],
  BaseColumnDefinitions[Column.LAST_MODIFIED],
  BaseColumnDefinitions[Column.CONSIGNED_FROM],
  BaseColumnDefinitions[saleLotCommentsColumnId],
  BaseColumnDefinitions[Column.XERO_VENDOR],
  BaseColumnDefinitions[Column.XERO_BUYER],
].map(colDef => ({ ...colDef, hide: true }) as SaleLotColDef); // hide by default rely on saved views to show;

const privateSaleSaleLotsColumns = (
  saleType: SaleType,
  speciesId: number,
  saleSubType: SaleSubType,
): SaleLotColDef[] => {
  const columns: SaleLotColDef[] = [
    {
      headerName: "Vendor",
      children: [
        BaseColumnDefinitions[Column.VENDOR_NAME],
        BaseColumnDefinitions[Column.VENDOR_SHORT_CODE],
        BaseColumnDefinitions[Column.VENDOR_PIC],
        BaseColumnDefinitions[Column.NVD],
        BaseColumnDefinitions[Column.SaleLot.VENDOR_COMMISSION],
        BaseColumnDefinitions[Column.SaleLot.PRIVATE_SALE_VENDOR_SPLITS],
        ...Object.values(VendorContactColumnDef),
        {
          headerName: "Address",
          children: Object.values(VendorAddressColumnDef),
        },
      ],
    },
    {
      headerName: "NVD",
      children: [
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_ID],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_VERSION],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_HC],
        {
          ...ConsignmentColumnDefsOnLot[Column.Consignment.NVD_RECEIVED],
          filterValueGetter: params => {
            const consignment = params.data?.consignment ?? params.data;
            return consignment?.hasNVDExceptions
              ? "Has NVD Exceptions"
              : "No NVD Exceptions";
          },
          filter: "agSetColumnFilter",
        },
        ConsignmentColumnDefsOnLot[Column.Consignment.ADDITIONAL_PICS],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_1],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_2],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_3],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_4],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_5],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_6],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_7],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_8],
        ConsignmentColumnDefsOnLot[Column.Consignment.NVD_QUESTION_9],
      ],
    },
    {
      headerName: "Buyer",
      children: [
        BaseColumnDefinitions[Column.BUYER_NAME],
        BaseColumnDefinitions[Column.BUYER_WAY_NAME],
        BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
        BaseColumnDefinitions[Column.SaleLot.BUYER_PIC],
        ...Object.values(BuyerContactColumnDef),
        {
          headerName: "Address",
          children: Object.values(BuyerAddressColumnDef),
        },
      ],
    },
    BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    BaseColumnDefinitions[Column.INVOICE_TO_CODE],
    BaseColumnDefinitions[Column.WEIGHT],
    BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
    BaseColumnDefinitions[Column.UNIT_PRICE],
    BaseColumnDefinitions[Column.RESERVE_PRICE],
    BaseColumnDefinitions[Column.TOTAL_PRICE],
    BaseColumnDefinitions[Column.GST_STATUS],
    saleType !== SaleType.OVER_HOOKS && BaseColumnDefinitions[Column.MARKS],
    BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
    BaseColumnDefinitions[Column.DIFF],
    BaseColumnDefinitions[Column.LABELS],
    BaseColumnDefinitions[Column.QUANTITY_TAGS_USED],
    BaseColumnDefinitions[Column.NO_TAG_COUNT],
    BaseColumnDefinitions[Column.NON_READER_COUNT],
    BaseColumnDefinitions[Column.DECEASED_COUNT],
    BaseColumnDefinitions[Column.SaleLot.SALE_NAME],
    BaseColumnDefinitions[Column.SaleLot.SALE_DATE],
    BaseColumnDefinitions[Column.QUANTITY],
    BaseColumnDefinitions[Column.LOT_NUMBER],
    {
      headerName: "Carrier Information",
      children: [
        BaseColumnDefinitions[Column.SaleLot.CARRIER_NAME],
        BaseColumnDefinitions[Column.SaleLot.CARRIER_CHARGE_INVOICE_NUMBER],
        BaseColumnDefinitions[Column.SaleLot.CARRIER_CHARGE_DELIVERY_DATE],
        BaseColumnDefinitions[Column.SaleLot.CARRIER_CHARGE_INVOICE_AMOUNT],
        BaseColumnDefinitions[Column.SaleLot.CARRIER_CHARGE_DELIVERY_NOTES],
      ],
    },
    BaseColumnDefinitions[Column.AGE_GROUP_NAME],
    BaseColumnDefinitions[Column.SaleLot.AGE_GROUP_CODE],
    BaseColumnDefinitions[Column.BREED_NAME],
    BaseColumnDefinitions[Column.SaleLot.BREED_CODE],
    BaseColumnDefinitions[Column.SaleLot.BREED_SALEYARD_CODE],
    BaseColumnDefinitions[Column.SaleLot.PRODUCT_CODE],
    BaseColumnDefinitions[Column.SaleLot.PRODUCT_SALEYARD_CODE],
    BaseColumnDefinitions[Column.SEX_NAME],
    saleLotDentitionColumns(speciesId, saleSubType?.enableDentition),
    BaseColumnDefinitions[Column.SCANNED_COUNT],
    BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
    BaseColumnDefinitions[Column.NOTE],
    BaseColumnDefinitions[Column.XERO_VENDOR],
    BaseColumnDefinitions[Column.XERO_VENDOR_NAME],
    BaseColumnDefinitions[Column.XERO_BUYER],
    BaseColumnDefinitions[Column.XERO_BUYER_NAME],
    ...saleLotAdvancedDraftingColumns(speciesId),
    {
      field: "saleLot",
      headerName: ACTION_COLUMN_NAME,
      cellRenderer: privateSaleActionsColumnRenderer,
      colId: ACTION_COLUMN_ID,
      pinned: "right",
      menuTabs: ["columnsMenuTab"],
    },
    BaseColumnDefinitions[saleLotCommentsColumnId],
  ];

  return columns.filter(Boolean);
};

export function saleLotColumns(
  saleType: SaleType,
  studFeaturesEnabled: boolean = false,
  penScanLotFeaturesEnabled: boolean = false,
  speciesId: number | null = null,
  saleSubType: SaleSubType,
): SaleLotColDef[] {
  if (saleType === SaleType.CLEARING) {
    return clearingSaleSaleLotColumns;
  } else if (
    saleType === SaleType.PADDOCK ||
    saleType === SaleType.OVER_HOOKS
  ) {
    return privateSaleSaleLotsColumns(saleType, speciesId, saleSubType);
  } else {
    return defaultSaleLotColumns(
      studFeaturesEnabled,
      penScanLotFeaturesEnabled,
      speciesId,
      saleSubType,
    );
  }
}
